// PolishButton.jsx — 一个字段旁的「AI 润色 / 还原我写的」（SB-067 一期，第六十四批）。
//
// 逻辑全在 pages/polish.mjs（纯模块 + 单测）；这里只负责渲染与一次网络调用。
// 三条设计，都是 SB-067 验收点名的：
//   · **替换前保留原稿并提供一键还原** —— 润色是"直接替换草稿供用户评判"，
//     那么不满意时必须能拿回自己写的那份，否则按一下就是不可逆的。
//   · **空值与过短防呆** —— 按钮直接禁用并给出理由，而不是让人按下去才吃一个 400。
//   · **剩余次数就在按钮旁** —— 额度用完前用户应当看得见它在减少。
import { useEffect, useState } from 'react';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { api } from '../api/client.mjs';
import { useT } from '../i18n/index.mjs';
import {
  EMPTY_POLISH, canRevert, polishErrorKey, polishFailed, polishReverted,
  polishStarted, polishSucceeded, polishability, remainingText,
} from '../pages/polish.mjs';

// quota 由**页面级**持有并传进来：同一页上可能有两三个润色按钮（标题、正文），
// 它们共享同一个额度池，各自去查会看到互相不一致的剩余数。
export default function PolishButton({ value, scene, onChange, quota, onQuota, disabled }) {
  const t = useT();
  const [state, setState] = useState(EMPTY_POLISH);
  const [lost, setLost] = useState(null);

  // 值被外部改回去（例如整表重置）时丢掉原稿：留着会让"还原"回到一个
  // 与当前上下文无关的旧文本。
  useEffect(() => {
    if (state.original !== null && value === state.original) setState(polishReverted(state));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const { enabled, reason } = polishability(value, scene, quota);
  const rem = remainingText(quota);

  const run = async () => {
    const before = String(value ?? '');
    setLost(null);
    setState((s) => polishStarted(s, before));
    try {
      const r = await api.polish(before, scene);
      onChange(r.polished);
      setState((s) => polishSucceeded(s));
      if (onQuota) onQuota({ limit: r.limit, remaining: r.remaining, used: r.used });
    } catch (e) {
      // 422 带 lost 列表：那是**数据不是话术**，原样显示（第 43 批语言规则）。
      // client.mjs 把响应体的整个 `error` 对象放在 ApiError.extra 上。
      const arr = e?.extra?.lost || null;
      setLost(Array.isArray(arr) && arr.length ? arr : null);
      setState((s) => polishFailed(s, polishErrorKey(e)));
    }
  };

  const revert = () => {
    onChange(state.original ?? '');
    setState((s) => polishReverted(s));
    setLost(null);
  };

  const btn = (
    <span>
      <Button size="small" variant="text" onClick={run}
              disabled={Boolean(disabled) || !enabled || state.busy}>
        {state.busy ? t('polish.busy') : t('polish.button')}
      </Button>
    </span>
  );

  return (
    <Box sx={{ mt: 0.5 }}>
      <Stack sx={{ alignItems: 'center', flexWrap: 'wrap' }} direction="row" spacing={1}>
        {/* 禁用的按钮自己说不出原因，所以理由挂在 Tooltip 上；无理由时不套 */}
        {reason ? <Tooltip title={t(reason)}>{btn}</Tooltip> : btn}
        {canRevert(state) ? (
          <Button size="small" variant="text" color="inherit" onClick={revert}>
            {t('polish.revert')}
          </Button>
        ) : null}
        {rem ? (
          <Typography variant="caption" color="text.secondary">
            {t('polish.remaining', { n: String(rem.remaining), limit: String(rem.limit) })}
          </Typography>
        ) : null}
      </Stack>
      {state.error ? (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {t(state.error)}
          {lost ? ` ${t('polish.altered.list', { items: lost.join('、') })}` : ''}
        </Typography>
      ) : null}
    </Box>
  );
}
